
import { useEffect, useRef, useMemo } from 'react';

interface NeuralNetworkBgProps {
  color?: string;
  nodeCount?: number;
  opacity?: number;
  className?: string;
  speed?: number;
  variant?: 'light' | 'dark';
}

interface Node {
  x: number;
  y: number;
  vx: number;
  vy: number;
  radius: number;
  pulseOffset: number;
  pulseSpeed: number;
}

interface Connection {
  from: number;
  to: number;
  signalOffset: number;
  signalSpeed: number;
}

export default function NeuralNetworkBg({
  color = '212,120,158',
  nodeCount = 40,
  opacity = 0.15,
  className = '',
  speed = 1,
  variant = 'dark',
}: NeuralNetworkBgProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animRef = useRef<number>(0);
  const nodesRef = useRef<Node[]>([]);
  const connectionsRef = useRef<Connection[]>([]);
  const timeRef = useRef(0);

  const maxDist = 180;

  const seedData = useMemo(() => {
    const nodes: Node[] = [];
    for (let i = 0; i < nodeCount; i++) {
      nodes.push({
        x: Math.random() * 2000,
        y: Math.random() * 1200,
        vx: (Math.random() - 0.5) * 0.3 * speed,
        vy: (Math.random() - 0.5) * 0.3 * speed,
        radius: Math.random() * 2 + 1,
        pulseOffset: Math.random() * Math.PI * 2,
        pulseSpeed: 0.5 + Math.random() * 1.5,
      });
    }

    const connections: Connection[] = [];
    for (let i = 0; i < nodes.length; i++) {
      for (let j = i + 1; j < nodes.length; j++) {
        const dx = nodes[i].x - nodes[j].x;
        const dy = nodes[i].y - nodes[j].y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist < maxDist * 1.5) {
          connections.push({
            from: i,
            to: j,
            signalOffset: Math.random() * Math.PI * 2,
            signalSpeed: 0.3 + Math.random() * 0.7,
          });
        }
      }
    }

    return { nodes, connections };
  }, [nodeCount, speed]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    nodesRef.current = seedData.nodes.map((n) => ({ ...n }));
    connectionsRef.current = seedData.connections.map((c) => ({ ...c }));

    const resize = () => {
      const rect = canvas.parentElement?.getBoundingClientRect();
      if (rect) {
        canvas.width = rect.width;
        canvas.height = rect.height;
      }
    };
    resize();
    window.addEventListener('resize', resize);

    const animate = () => {
      if (!canvas || !ctx) return;
      const w = canvas.width;
      const h = canvas.height;
      timeRef.current += 0.016;
      const t = timeRef.current;

      ctx.clearRect(0, 0, w, h);

      const nodes = nodesRef.current;

      // Update node positions
      for (const node of nodes) {
        node.x += node.vx;
        node.y += node.vy;

        if (node.x < -20) node.x = w + 20;
        if (node.x > w + 20) node.x = -20;
        if (node.y < -20) node.y = h + 20;
        if (node.y > h + 20) node.y = -20;
      }

      const rgb = color;
      const isLight = variant === 'light';

      // Draw connections
      for (const conn of connectionsRef.current) {
        const a = nodes[conn.from];
        const b = nodes[conn.to];
        const dx = a.x - b.x;
        const dy = a.y - b.y;
        const dist = Math.sqrt(dx * dx + dy * dy);

        if (dist < maxDist) {
          const alpha = (1 - dist / maxDist) * (isLight ? 0.12 : 0.2);

          // Draw dendrite line
          ctx.beginPath();
          ctx.moveTo(a.x, a.y);
          ctx.lineTo(b.x, b.y);
          ctx.strokeStyle = `rgba(${rgb}, ${alpha})`;
          ctx.lineWidth = 0.5;
          ctx.stroke();

          // Traveling signal particle
          const signalPos = (Math.sin(t * conn.signalSpeed + conn.signalOffset) + 1) / 2;
          const sx = a.x + (b.x - a.x) * signalPos;
          const sy = a.y + (b.y - a.y) * signalPos;
          const signalAlpha = (1 - dist / maxDist) * (isLight ? 0.25 : 0.5);

          ctx.beginPath();
          ctx.arc(sx, sy, 1.2, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(${rgb}, ${signalAlpha})`;
          ctx.fill();
        }
      }

      // Draw neuron nodes
      for (const node of nodes) {
        const pulse = Math.sin(t * node.pulseSpeed + node.pulseOffset);
        const r = node.radius + pulse * 0.8;
        const nodeAlpha = isLight ? 0.15 + pulse * 0.1 : 0.3 + pulse * 0.2;

        // Outer glow
        const gradient = ctx.createRadialGradient(
          node.x, node.y, 0,
          node.x, node.y, r * 4
        );
        gradient.addColorStop(0, `rgba(${rgb}, ${nodeAlpha * 0.6})`);
        gradient.addColorStop(1, `rgba(${rgb}, 0)`);
        ctx.beginPath();
        ctx.arc(node.x, node.y, r * 4, 0, Math.PI * 2);
        ctx.fillStyle = gradient;
        ctx.fill();

        // Core
        ctx.beginPath();
        ctx.arc(node.x, node.y, r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${rgb}, ${nodeAlpha})`;
        ctx.fill();
      }

      animRef.current = requestAnimationFrame(animate);
    };

    animRef.current = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(animRef.current);
      window.removeEventListener('resize', resize);
    };
  }, [seedData, color, variant]);

  return (
    <canvas
      ref={canvasRef}
      className={`absolute inset-0 pointer-events-none ${className}`}
      style={{ opacity }}
      aria-hidden="true"
    />
  );
}
